import type {
  ChatState,
  Turn,
  ActiveTurn,
  PendingMessage,
  StringOrMarkdown,
  ToolCallPendingConfirmationState,
  ToolCallPendingResultConfirmationState,
  ToolCallState,
  ToolResultContent,
  ToolResultSubagentContent,
  ChatInputRequest,
  ChatInputQuestion,
  ChatInputSingleSelectQuestion,
  ChatInputMultiSelectQuestion,
  ResponsePart,
} from '@microsoft/agent-host-protocol'
import type {RelayStatus} from './relay-connection-runtime'

export type TimelineView = 'ready' | 'error' | 'loading'

// AHP enums are const — compare wire literals (isolatedModules).
const CONTENT_TYPE_SUBAGENT = 'subagent'
const QUESTION_KIND_SINGLE_SELECT = 'single-select'
const QUESTION_KIND_MULTI_SELECT = 'multi-select'
const CONTENT_TYPE_TEXT = 'text'
const RESPONSE_PART_KIND_INPUT_REQUEST = 'inputRequest'
const STATUS_PENDING_CONFIRMATION = 'pending-confirmation'
const STATUS_PENDING_RESULT_CONFIRMATION = 'pending-result-confirmation'
const OPTION_KIND_APPROVE = 'approve'

type ConfirmableToolCallState = ToolCallPendingConfirmationState | ToolCallPendingResultConfirmationState
type InputRequestResponsePart = Extract<ResponsePart, {request: ChatInputRequest}>

export interface PendingToolConfirmationOption {
  id: string
  label: string
  kind: typeof OPTION_KIND_APPROVE | 'deny'
  selectedOptionId?: string
}

export interface ActivePendingToolConfirmation {
  turnId: string
  toolCallId: string
  title: string
  detail?: string
  options: PendingToolConfirmationOption[]
  resultConfirmation: boolean
}

/**
 * A reference to a tool-spawned subagent (worker chat), extracted from an AHP
 * `ToolResultSubagentContent` block on a tool call.
 */
export interface SubagentHandle {
  /** Worker chat URI — subscribable for the subagent's full timeline. */
  resource: string
  /** Display title for the subagent. */
  title: string
  /** Internal agent name, when the host provides one. */
  agentName?: string
}

/** Narrow a tool-result content block to the subagent delegation variant. */
function isSubagentContent(block: ToolResultContent): block is ToolResultSubagentContent {
  return block.type === CONTENT_TYPE_SUBAGENT
}

/**
 * Extract the subagent reference embedded in a tool call's result, if the call
 * spawned one.
 */
export function extractSubagent(toolCall: ToolCallState): SubagentHandle | undefined {
  const content = 'content' in toolCall ? toolCall.content : undefined
  const block = content?.find(isSubagentContent)
  if (!block?.resource) return undefined

  return {
    resource: block.resource,
    title: block.title?.trim() ? block.title : block.resource,
    ...(block.agentName ? {agentName: block.agentName} : {}),
  }
}

/**
 * `error`/`closed` without state means the relay failed before seeding —
 * otherwise the timeline would spin on "Loading…" forever.
 */
export function resolveTimelineView(hasState: boolean, status: RelayStatus | 'idle', error?: string): TimelineView {
  if (hasState) return 'ready'
  if (error || status === 'error' || status === 'closed') return 'error'
  return 'loading'
}

export function stringOrMarkdownToText(value: StringOrMarkdown | undefined): string | undefined {
  if (value === undefined) return undefined
  return typeof value === 'string' ? value : value.markdown
}

export function isPendingToolConfirmation(toolCall: ToolCallState): toolCall is ConfirmableToolCallState {
  return toolCall.status === STATUS_PENDING_CONFIRMATION || toolCall.status === STATUS_PENDING_RESULT_CONFIRMATION
}

function extractToolResultText(content: ToolResultContent[] | undefined): string {
  if (!content) return ''
  return content
    .filter((block): block is Extract<ToolResultContent, {type: 'text'}> => block.type === CONTENT_TYPE_TEXT)
    .map(block => block.text)
    .join('')
}

function getConfirmationTitle(toolCall: ConfirmableToolCallState): string {
  if ('confirmationTitle' in toolCall) {
    return (
      stringOrMarkdownToText(toolCall.confirmationTitle) ??
      stringOrMarkdownToText(toolCall.invocationMessage) ??
      toolCall.displayName
    )
  }

  return stringOrMarkdownToText(toolCall.invocationMessage) ?? toolCall.displayName
}

function getConfirmationDetail(toolCall: ConfirmableToolCallState): string | undefined {
  const toolInput = 'toolInput' in toolCall ? toolCall.toolInput : undefined

  if (toolCall.status === STATUS_PENDING_RESULT_CONFIRMATION) {
    return extractToolResultText(toolCall.content) || toolInput || stringOrMarkdownToText(toolCall.invocationMessage)
  }

  return toolInput || stringOrMarkdownToText(toolCall.invocationMessage)
}

function getRiskAssessmentDetail(toolCall: ConfirmableToolCallState): string | undefined {
  if (toolCall.status !== STATUS_PENDING_CONFIRMATION || !toolCall.riskAssessment) return undefined
  if (toolCall.riskAssessment.status === 'loading') return 'Risk assessment in progress.'
  return `Risk assessment: ${stringOrMarkdownToText(toolCall.riskAssessment.reason)}`
}

function getConfirmationOptions(toolCall: ConfirmableToolCallState): PendingToolConfirmationOption[] {
  if ('options' in toolCall && toolCall.options && toolCall.options.length > 0) {
    return toolCall.options.map(option => ({
      id: option.id,
      label: option.label,
      kind: option.kind === OPTION_KIND_APPROVE ? OPTION_KIND_APPROVE : 'deny',
      selectedOptionId: option.id,
    }))
  }

  return [
    {id: 'approve', label: 'Allow', kind: OPTION_KIND_APPROVE},
    {id: 'deny', label: 'Deny', kind: 'deny'},
  ]
}

export type TimelineTurn = Turn | ActiveTurn

export function getTurns(state: ChatState): TimelineTurn[] {
  const turns: TimelineTurn[] = [...(state.turns ?? [])]
  // A reconnect snapshot (or a turn-completion race) can report the same turn in
  // both `turns` and `activeTurn`. Appending it unconditionally would render the
  // turn — and its feedback bar — twice, so skip the active turn when a turn with
  // the same id is already in history.
  const {activeTurn} = state
  if (activeTurn && !turns.some(turn => turn.id === activeTurn.id)) {
    turns.push(activeTurn)
  }
  return turns
}

export function isWorking(state: ChatState): boolean {
  return !!state.activeTurn
}

/** Messages queued to run as their own turns once the current turn finishes. */
export function getQueuedMessages(state: ChatState): PendingMessage[] {
  return state.queuedMessages ?? []
}

function getPendingToolConfirmation(
  toolCall: ToolCallState,
  turnId: string,
): ActivePendingToolConfirmation | undefined {
  if (!isPendingToolConfirmation(toolCall)) return undefined

  const detail = getConfirmationDetail(toolCall)
  const riskAssessmentDetail = getRiskAssessmentDetail(toolCall)
  return {
    turnId,
    toolCallId: toolCall.toolCallId,
    title: getConfirmationTitle(toolCall),
    detail: [detail, riskAssessmentDetail].filter(Boolean).join(' ') || undefined,
    options: getConfirmationOptions(toolCall),
    resultConfirmation: toolCall.status === STATUS_PENDING_RESULT_CONFIRMATION,
  }
}

export function getActivePendingToolConfirmation(state: ChatState): ActivePendingToolConfirmation | undefined {
  const activeTurn = state.activeTurn
  if (!activeTurn) return undefined

  for (const part of activeTurn.responseParts) {
    if (part.kind !== 'toolCall') continue

    const pendingConfirmation = getPendingToolConfirmation(part.toolCall, activeTurn.id)
    if (pendingConfirmation) return pendingConfirmation
  }

  return undefined
}

export function needsInput(state: ChatState): boolean {
  if (getInputRequests(state).length > 0) return true

  return !!getActivePendingToolConfirmation(state)
}

export function getActivity(state: ChatState): string | undefined {
  return state.activity
}

function isUnresolvedInputRequest(part: ActiveTurn['responseParts'][number]): part is InputRequestResponsePart {
  return part.kind === RESPONSE_PART_KIND_INPUT_REQUEST && part.response === undefined
}

/** Pending input requests currently blocking or informing the active turn. */
export function getInputRequests(state: ChatState): ChatInputRequest[] {
  return state.activeTurn?.responseParts.filter(isUnresolvedInputRequest).map(part => part.request) ?? []
}

/** A select-style question exposes `options`; narrow to that variant. */
function asSelectQuestion(
  question: ChatInputQuestion,
): ChatInputSingleSelectQuestion | ChatInputMultiSelectQuestion | undefined {
  if (question.kind === QUESTION_KIND_SINGLE_SELECT || question.kind === QUESTION_KIND_MULTI_SELECT) {
    return question
  }
  return undefined
}

/**
 * A framework-neutral projection of one AHP {@link ChatInputRequest} for
 * interactive rendering. Consumers (React composers, other AHP clients) map
 * this onto their own prompt UI without depending on the wire shapes.
 */
export interface NormalizedInputRequest {
  /** Stable request id (`chat/inputCompleted.requestId`). */
  requestId: string
  /** Question id used as the key in the completed answers map. */
  questionId: string
  /** Prompt shown to the user. */
  question: string
  /** Selectable option labels (empty for free-text or elicitation prompts). */
  choices: string[]
  /** Whether multiple options may be selected. */
  multiSelect: boolean
  /** Whether the user may type a freeform answer. */
  allowFreeform: boolean
  /** The recommended option label, when the request marks one. */
  recommendedChoice?: string
  /** True when this request carries multiple questions (elicitation-style form). */
  isElicitation: boolean
}

/**
 * Project an AHP {@link ChatInputRequest} onto a {@link NormalizedInputRequest}.
 *
 * Only the first question's chrome (prompt/options) is surfaced for the common
 * single-question `ask_user` case; multi-question requests are flagged via
 * `isElicitation` so the caller can route them to a form renderer.
 */
export function normalizeInputRequest(request: ChatInputRequest): NormalizedInputRequest | undefined {
  const questions = request.questions ?? []
  const first = questions[0]
  if (!first) return undefined

  const select = asSelectQuestion(first)
  const options = select?.options ?? []
  const recommended = options.find(option => option.recommended)

  return {
    requestId: request.id,
    questionId: first.id,
    question: request.message?.trim() || first.message,
    choices: options.map(option => option.label),
    multiSelect: first.kind === QUESTION_KIND_MULTI_SELECT,
    allowFreeform: select ? Boolean(select.allowFreeformInput) : first.kind === 'text',
    ...(recommended ? {recommendedChoice: recommended.label} : {}),
    // Multi-question requests, or a single multi-select (which the simple
    // ask_user prompt can't express — it only sends one `selected` value), are
    // routed through the elicitation form path so multi-select answers become
    // `selected-many`.
    isElicitation: questions.length > 1 || first.kind === QUESTION_KIND_MULTI_SELECT,
  }
}

/** Map an option label back to its stable option id for a given request. */
export function optionIdForLabel(request: ChatInputRequest, questionId: string, label: string): string | undefined {
  const question = (request.questions ?? []).find(q => q.id === questionId)
  const select = question ? asSelectQuestion(question) : undefined
  return select?.options.find(option => option.label === label)?.id
}
